import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import memoize from '@github/memoize/decorator';
import { CurrencyFormats } from '@macs-frontend/web-macs/shared';
import * as accounting from 'accounting-js';
import { merge, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import {
  CurrentCompanyService,
  defaultCurrency,
  defaultNumberFormat,
} from '../../../core';
import { Currency } from '../models/currency.model';

interface CurrencyConfiguration {
  format: string;
  symbol: string;
  appendSymbol: string;
}

@Pipe({ name: 'companyCurrency', pure: false })
export class CompanyCurrencyPipe implements PipeTransform, OnDestroy {
  destroy$: Subject<any> = new Subject();
  currency: Currency = defaultCurrency;
  currencyFormat = CurrencyFormats.SYMBOL_BEFORE;
  numberFormat = defaultNumberFormat;
  // @ts-expect-error
  format: string;
  // @ts-expect-error
  symbol: string;
  // @ts-expect-error
  thousand: string;
  // @ts-expect-error
  decimal: string;
  // @ts-expect-error
  appendSymbol: string;

  constructor(
    private currentCompanyService: CurrentCompanyService,
    private cdr: ChangeDetectorRef,
  ) {
    merge(
      this.currentCompanyService.currentCompany$,
      this.currentCompanyService.currentCompanyPublic$,
    )
      .pipe(
        takeUntil(this.destroy$),
        filter(company => !!company),
        tap(company => {
          this.currency = company.currency ? company.currency : this.currency;
          this.numberFormat = company.numberFormat
            ? company.numberFormat
            : this.numberFormat;
          this.currencyFormat = company.currencyFormat
            ? (company.currencyFormat as CurrencyFormats)
            : this.currencyFormat;
          this.setConfiguration();
          this.cdr.markForCheck();
        }),
      )
      .subscribe();

    this.setConfiguration();
  }

  getCurrencyConfiguration(
    currencyFormat?: CurrencyFormats,
    customCurrency?: Currency,
  ): CurrencyConfiguration {
    const currencyConfiguration: CurrencyConfiguration = {
      // @ts-expect-error
      format: null,
      // @ts-expect-error
      symbol: null,
      appendSymbol: '',
    };
    const currency: Currency = customCurrency ? customCurrency : this.currency;
    // The company payload can omit `sign` on its currency (seen on dev),
    // which would render cells as "undefined 200,00". Fall back to the
    // default currency's symbol/code.
    const sign = currency.sign || defaultCurrency.sign || '';
    const code = currency.code || defaultCurrency.code;
    switch (currencyFormat || this.currencyFormat) {
      case CurrencyFormats.SYMBOL_BEFORE:
        currencyConfiguration.format = '%s %v';
        currencyConfiguration.symbol = sign;
        break;
      case CurrencyFormats.SYMBOL_AFTER:
        currencyConfiguration.format = '%v %s';
        currencyConfiguration.symbol = sign;
        break;

      case CurrencyFormats.ISO_CODE_BEFORE:
        currencyConfiguration.format = '%s %v';
        currencyConfiguration.symbol = code;
        break;
      case CurrencyFormats.ISO_CODE_AFTER:
        currencyConfiguration.format = '%v %s';
        currencyConfiguration.symbol = code;
        break;

      case CurrencyFormats.ISO_CODE_BEFORE_SYMBOL_BEFORE:
        currencyConfiguration.format = '%s %v';
        currencyConfiguration.symbol = code + sign;
        break;
      case CurrencyFormats.SYMBOL_AFTER_ISO_CODE_AFTER:
        currencyConfiguration.format = '%v %s';
        currencyConfiguration.symbol = sign + code;
        break;

      case CurrencyFormats.ISO_CODE_BEFORE_SYMBOL_AFTER:
        currencyConfiguration.format = '%s %v';
        currencyConfiguration.symbol = code;
        this.appendSymbol = ` ${sign}`;
        break;
      case CurrencyFormats.SYMBOL_BEFORE_ISO_CODE_AFTER:
        currencyConfiguration.format = '%s %v';
        currencyConfiguration.symbol = sign;
        currencyConfiguration.appendSymbol = ` ${code}`;
        break;
    }
    return currencyConfiguration;
  }

  setConfiguration() {
    const currencyConfiguration: CurrencyConfiguration = this.getCurrencyConfiguration();
    this.thousand = this.numberFormat.charAt(1);
    this.decimal = this.numberFormat.charAt(5);
    this.format = currencyConfiguration.format;
    this.symbol = currencyConfiguration.symbol;
    this.appendSymbol = currencyConfiguration.appendSymbol;
  }

  @memoize()
  transform(
    value: any,
    showCurrencySign = false,
    precision = 2,
    format?: CurrencyFormats,
    currency?: Currency,
  ): string {
    const currencyConfiguration = format
      ? this.getCurrencyConfiguration(format, currency)
      : {
          format: this.format,
          symbol: this.symbol,
          appendSymbol: this.appendSymbol,
        };
    return (
      accounting.formatMoney(value || 0, {
        symbol: currencyConfiguration.symbol,
        format: showCurrencySign ? currencyConfiguration.format : '%v',
        precision,
        thousand: this.thousand,
        decimal: this.decimal,
      }) + (showCurrencySign ? currencyConfiguration.appendSymbol : '')
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
